import { gql, TypedDocumentNode } from "@apollo/client";
import type { PgmLevelCatalogEntry } from "@/features/person/utils/education-program-level-options";

export const GET_PGM_PROGRAM_TABLE_DATA_QUERY = gql`
  query GetPGMProgramTableData($pgaId: ID!, $includeArchived: Boolean) {
    person(pgaId: $pgaId) {
      id
      pgaId
      hasArchivedEducationPgmProgramHistory
      lastAttendedPgmUniversity {
        id
        legacyId
        name
      }
      educationLatestPgmProgram {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        pgmProgramCompleted
        pgmProgramExpirationDate
      }
      educationPgmProgramHistory(includeArchived: $includeArchived) {
        archived
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        completeCode
        remarks
        pgmProgramExpirationDate
        levelExpirationDate
        programRequirements {
          archived
          requireId
          notes
          remarks
          score
          levelCode
          completionStatus
          completionDate
          isCompleted
          isInProgress
          createDate
          requirementDefinition {
            levelCode
            courseDescription
            courseId
            description
            requireId
            requireType
          }
        }
        testHistory {
          attemptNumber
          completionStatus
          isFailed
          isPassed
          isRetake
          levelCode
          maxScore
          score
          status
          testDate
          testId
          testDefinition {
            description
            levelCode
            maxScore
            oldTestId
            testId
          }
        }
      }
      prospectProgram {
        playingAbilityTestStatus {
          status
          startDate
          expirationDate
          expired
          completed
          submissions {
            date
            expirationDate
            expired
            result
            patType
          }
          activeSuspension {
            startDate
            endDate
          }
        }
      }
    }
  }
`;

export interface PGMProgramTableData {
  person: {
    id: string;
    pgaId: string;
    hasArchivedEducationPgmProgramHistory: boolean;
    lastAttendedPgmUniversity?: {
      id: string;
      legacyId: string;
      name: string;
    } | null;
    educationLatestPgmProgram: {
      curriculumVersion: string;
      startDate: string;
      endDate?: string | null;
      level: number;
      levelCode: string;
      careerPath?: string | null;
      programType: string;
      completed: boolean;
      pgmProgramCompleted?: boolean;
      pgmProgramExpirationDate?: string | null;
    } | null;
    educationPgmProgramHistory: Array<{
      archived: boolean;
      curriculumVersion: string;
      startDate: string;
      endDate?: string | null;
      level: number;
      levelCode: string;
      careerPath?: string | null;
      programType: string;
      completed: boolean;
      completeCode?: string | null;
      remarks?: string | null;
      pgmProgramExpirationDate?: string | null;
      levelExpirationDate?: string | null;
      programRequirements: Array<{
        archived: boolean;
        requireId: string;
        notes?: string | null;
        remarks?: string | null;
        score?: number | null;
        levelCode: string;
        completionStatus: string;
        completionDate?: string | null;
        isCompleted: boolean;
        isInProgress: boolean;
        createDate: string;
        requirementDefinition: {
          levelCode: string;
          courseDescription?: string | null;
          courseId?: string | null;
          description: string;
          requireId: string;
          requireType: string;
        };
      }>;
      testHistory: Array<{
        attemptNumber: number;
        completionStatus: string;
        isFailed: boolean;
        isPassed: boolean;
        isRetake: boolean;
        levelCode: string;
        maxScore: number;
        score: number;
        status: string;
        testDate: string;
        testId: string;
        testDefinition: {
          description: string;
          levelCode: string;
          maxScore: number;
          oldTestId?: string | null;
          testId: string;
        };
      }>;
    }>;
    prospectProgram?: {
      playingAbilityTestStatus?: {
        status: string;
        startDate?: string | null;
        expirationDate?: string | null;
        completed: boolean;
        submissions?: Array<{
          date: string;
          result: string;
          patType: string;
        }>;
        activeSuspension?: {
          startDate: string;
          endDate: string;
        };
      } | null;
    } | null;
  };
}

export type EducationProgramLevelsResponse = {
  educationProgramLevels: PgmLevelCatalogEntry[];
};

export const GET_EDUCATION_PROGRAM_LEVELS_QUERY: TypedDocumentNode<EducationProgramLevelsResponse> = gql`
  query GetEducationProgramLevels {
    educationProgramLevels {
      levelCode
      level
      careerPath
      curriculumVersion
    }
  }
`;
